import { createService } from "./createService"
import { toPaymentSchedulePayload } from "./paymentSchedulePayload"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.paymentSchedules)

export const PAYMENT_SCHEDULES_INSTANCE = service.instanceKey

export const PAYMENT_SCHEDULES_ENDPOINTS = {
  IMPORT_CUSTOM: "/admin/payment_schedules/import_custom_payment_schedule",
  CHECK_NAME: "/admin/payment_schedules/check_custom_payment_schedule",
  DOWNLOAD_BULK_CUSTOM_FORM:
    "/admin/payment_schedules/view/download_bulk_custom_schedule_upload_form",
  DOWNLOAD_BULK_PAYMENT_CSV:
    "/admin/payment_schedules/view/download_bulk_payment_csv_file",
  DOWNLOAD_BULK_PAYMENT_CSV_INCLUDE_DOWNLOADED:
    "/admin/payment_schedules/view/download_bulk_payment_csv_file_include_downloaded",
  DOWNLOAD_BULK_PAYMENT_XLS:
    "/admin/payment_schedules/view/download_bulk_payment_xls_file",
  DOWNLOAD_BULK_PAYMENT_XLS_INCLUDE_DOWNLOADED:
    "/admin/payment_schedules/view/download_bulk_payment_xls_file_include_downloaded",
  REDOWNLOAD_BULK_PAYMENT_CSV:
    "/admin/payment_schedules/view/redownload_bulk_payment_csv_file",
  REDOWNLOAD_BULK_PAYMENT_XLS:
    "/admin/payment_schedules/view/redownload_bulk_payment_xls_file",
  SELECT_REGULAR_PAYMENT_SCHEDULES:
    "/admin/payment_schedules/selectregularpaymentschedules",
  SELECT_CUSTOM_PAYMENT_SCHEDULES:
    "/admin/payment_schedules/selectcustompaymentschedules",
  SELECT_PAYMENT_REVERSALS: "/admin/payment_schedules/selectpaymentreversals",
  DELETE_PAYMENT_REVERSALS: "/admin/payment_schedules/delete_payment_reversals",
  DELETE_PAYMENT_SCHEDULES: "/admin/payment_schedules/delete_payment_schedule",
  VIEW_CURRENCIES: "/admin/payment_schedules/view/currencies",
  VIEW_CURRENCIES_APPROVAL: "/payment_schedules/view/currencies",
  VIEW_PAYMENT_SCHEDULES: "/admin/payment_schedules/view/index",
  VIEW_SCHEDULE_DETAILS: "/admin/payment_schedules/view/get_schedule_details",
  VIEW_SCHEDULE_DETAILS_APPROVAL: "/payment_schedules/view/details",
  VIEW_SCHEDULE_REPORT: "/admin/reports/view/schedule",
  GET_PAYSCHEDULE_BENEFICIARIES: "/admin/people/getpayschedulebeneficiaries",
  GET_TRANSFER_DETAILS: "/admin/payment_schedules/gettransferdetails",
  // batch (newArchAxiosInstance — already in use by UI, kept here for reference)
  BATCH_UPDATE: "/admin/payment_schedules/batch_update_payment_schedule_items",
  BATCH_REVIEW: "/admin/payment_schedules/batch_review_payment_schedule",
  SELECT_STATUTORY_ITEMS:
    "/admin/payment_schedules/selectpaymentscheduleitems_statutory",
  UPDATE_PAYSCHEDULE_ITEMS:
    "/admin/payment_schedules/update_payment_schedule_items",
  SELECT_PAYOUT_ITEMS:
    "/admin/payment_schedules/selectpaymentscheduleitems_payout",
  SELECT_EWA_ITEMS: "/admin/payment_schedules/selectpaymentscheduleitems_ewa",
  BATCH_UNUPDATE:
    "/admin/payment_schedules/batch_unupdate_payment_schedule_items",
  UPDATE_ITEM_DETAILS:
    "/admin/payment_schedules/update_payment_schedule_item_details",
  UPDATE_CUSTOM_SCHEDULE:
    "/admin/payment_schedules/update_custom_payment_schedule",
  UPLOAD_STATUTORY_SCHEDULE:
    "/admin/payment_schedules/upload_statutory_schedule",
  REFRESH_ITEMS: "/admin/payment_schedules/refresh_payment_schedule_items_new",
  REFRESH_STATUTORY_ITEMS:
    "/admin/payment_schedules/refresh_payment_schedule_statutory_items",
  PROCESS_PAYMENT: "/admin/payment_schedules/process_payment_schedule",
  RESET_ITEMS: "/admin/payment_schedules/reset_payment_schedule_items",
  REVERSE_ITEMS: "/admin/payment_schedules/reverse_payment_schedule_items",
  UNREVERSE_ITEMS: "/admin/payment_schedules/unreverse_payment_schedule_items",
  RETRY_FAILED_PAYMENTS: "/admin/payment_schedules/retry_failed_payments",
  // create regular payment schedule
  CREATE_REGULAR: "/admin/payment_schedules/create_regular_payment_schedule",
  CREATE_CUSTOM: "/admin/payment_schedules/create_custom_payment_schedule",
  // single-item
  REMOVE_ITEM: "/admin/payment_schedules/remove_payment_schedule_item",
  RESTORE_ITEM: "/admin/payment_schedules/restore_payment_schedule_item",
  REFRESH_ITEM: "/admin/payment_schedules/refresh_payment_schedule_item_new",
  RESET_ITEM: "/admin/payment_schedules/reset_payment_schedule_item",
  RETRY_FAILED_PAYMENT: "/admin/payment_schedules/retry_failed_payment",
  REVERSE_ITEM: "/admin/payment_schedules/reverse_payment_schedule_item",
  UNREVERSE_ITEM: "/admin/payment_schedules/unreverse_payment_schedule_item",
  UPDATE_ITEM_DETAIL:
    "/admin/payment_schedules/update_payment_schedule_item_detail",
  REVIEW_PAYMENT_SCHEDULE: "/admin/payment_schedules/review_payment_schedule",
  REVIEW_PAYMENT_SCHEDULE_APPROVAL:
    "/payment_schedules/review_payment_schedule",
  SELECT_CUSTOM_APPROVAL: "/payment_schedules/selectcustompaymentschedules",
  REVIEW_CUSTOM_APPROVAL: "/payment_schedules/review_payment_schedule",
  BATCH_REVIEW_CUSTOM_APPROVAL:
    "/payment_schedules/batch_review_payment_schedule",
  REVIEW_CUSTOM: "/admin/payment_schedules/review_payment_schedule",
  BATCH_REVIEW_CUSTOM: "/admin/payment_schedules/batch_review_payment_schedule",
} as const

/** One line of the create-custom-payment-schedule request body. */
export interface ICreateCustomPaymentScheduleItem {
  advanceTimeSheetPayrollRunId: number
  advanceTimeSheetPayrollRunItemId: number
  description: string
  person_id: string
  contractor_id: null
  amount: string
  source_currency_code: string
  state: null
}

export const paymentSchedulesService = {
  instanceKey: service.instanceKey,

  importCustom: (formData: FormData) =>
    service.upload(PAYMENT_SCHEDULES_ENDPOINTS.IMPORT_CUSTOM, formData),

  checkName: (description: string, paymentScheduleId: string | number) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.CHECK_NAME, {
      description,
      payment_schedule_id: paymentScheduleId,
    }),
  processPayment: (
    id: string | number | undefined,
    paymentOption: string,
    currencyCode: string | undefined
  ) =>
    service.post(PAYMENT_SCHEDULES_ENDPOINTS.PROCESS_PAYMENT, {
      id,
      payment_option: paymentOption,
      currency_code: currencyCode,
    }),

  updateScheduleItems: (
    action: "select" | "deselect",
    currencyCode: string | undefined,
    body: any
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UPDATE_PAYSCHEDULE_ITEMS}?action=${action}&currency_code=${currencyCode}`,
      body
    ),

  selectPayoutItems: (params: Record<string, any>) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYOUT_ITEMS, params),
  selectEwaItems: (id: string) =>
    service.get(`${PAYMENT_SCHEDULES_ENDPOINTS.SELECT_EWA_ITEMS}?id=${id}`),

  updateCustomSchedule: (id: string, body: any) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UPDATE_CUSTOM_SCHEDULE}?id=${id}`,
      toPaymentSchedulePayload(body)
    ),

  getPayScheduleBeneficiaries: (params?: Record<string, any>) =>
    service.post(
      PAYMENT_SCHEDULES_ENDPOINTS.GET_PAYSCHEDULE_BENEFICIARIES,
      params
    ),

  getTransferDetails: (id: string | null, currencyCode: string | undefined) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.GET_TRANSFER_DETAILS}?id=${id}&x=${currencyCode}`
    ),
  downloadBulkCustomForm: (includeExited = false) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.DOWNLOAD_BULK_CUSTOM_FORM, {
      include_exited: includeExited,
    }),
  selectRegularPaymentSchedules: (body: any) =>
    service.get(
      PAYMENT_SCHEDULES_ENDPOINTS.SELECT_REGULAR_PAYMENT_SCHEDULES,
      body
    ),
  selectPaymentReversals: (params: Record<string, any>) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYMENT_REVERSALS, params),
  deletePaymentReversals: () =>
    service.post(PAYMENT_SCHEDULES_ENDPOINTS.DELETE_PAYMENT_REVERSALS),

  deletePaymentSchedules: (activeDeletId: string) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.DELETE_PAYMENT_SCHEDULES}?id=${activeDeletId}`
    ),

  viewCurrencies: (x: string | null, s?: string) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_CURRENCIES}?id=${x}${
        s ? `&s=${s}` : ``
      }`
    ),
  viewCurrenciesApproval: (x: string | null) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_CURRENCIES_APPROVAL}?id=${x}`
    ),

  viewPaymentSchedules: (
    id: string,
    x: string | null,
    currencyCode?: string | undefined
  ) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_PAYMENT_SCHEDULES}?id=${id}&x=${x}${
        currencyCode ? `&s=${currencyCode}` : ""
      }`
    ),

  viewScheduleDetails: (x: string | null) =>
    service.get(`${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_SCHEDULE_DETAILS}?id=${x}`),
  viewScheduleDetailsApproval: (x: string | null) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_SCHEDULE_DETAILS_APPROVAL}?id=${x}`
    ),
  viewScheduleReport: (
    id: string | null,
    itemTypeId: string | number,
    x: boolean
  ) =>
    service.get(
      `${PAYMENT_SCHEDULES_ENDPOINTS.VIEW_SCHEDULE_REPORT}?id=${id}&payment_schedule_item_type_id=${itemTypeId}&x=${x}`
    ),

  refreshStatutoryItems: (body: Record<string, any>) =>
    service.post(PAYMENT_SCHEDULES_ENDPOINTS.REFRESH_STATUTORY_ITEMS, body),
  batchUpdate: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.BATCH_UPDATE}?id=${id}&currency_code=${currencyCode}`
    ),
  batchUnupdate: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.BATCH_UNUPDATE}?id=${id}&currency_code=${currencyCode}`
    ),
  batchReview: (type: "approve" | "decline", body: Record<string, any>[]) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.BATCH_REVIEW}?id=${
        type === "approve" ? "Approve" : "Decline"
      }`,
      body
    ),
  updateItemDetails: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UPDATE_ITEM_DETAILS}?id=${id}&currency_code=${currencyCode}`
    ),
  uploadStatutorySchedule: (body: Record<string, any>) =>
    service.post(PAYMENT_SCHEDULES_ENDPOINTS.UPLOAD_STATUTORY_SCHEDULE, body),
  selectStatutoryItems: (params: Record<string, any>) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.SELECT_STATUTORY_ITEMS, params),
  refreshItems: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REFRESH_ITEMS}?id=${id}&currency_code=${currencyCode}`
    ),
  resetItems: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.RESET_ITEMS}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  reverseItems: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVERSE_ITEMS}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  unreverseItems: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UNREVERSE_ITEMS}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  retryFailedPayment: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.RETRY_FAILED_PAYMENT}?id=${id}&currency_code=${currencyCode}`
    ),

  // single-item operations
  removeItem: (id: string | number, comment: string) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REMOVE_ITEM}?id=${id}&comment=${comment}`
    ),
  restoreItem: (id: string | number, comment: string) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.RESTORE_ITEM}?id=${id}&comment=${comment}`
    ),
  refreshItem: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REFRESH_ITEM}?id=${id}&currency_code=${currencyCode}`
    ),
  resetItem: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.RESET_ITEM}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  retryFailedPayments: (
    id: string | number,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.RETRY_FAILED_PAYMENTS}?id=${id}&currency_code=${currencyCode}`
    ),
  reverseItem: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVERSE_ITEM}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  unreverseItem: (
    id: string | number,
    comment: string,
    currencyCode: string | undefined
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UNREVERSE_ITEM}?id=${id}&comment=${comment}&currency_code=${currencyCode}`
    ),
  updateItemDetail: (id: string | number, currencyCode: string | undefined) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.UPDATE_ITEM_DETAIL}?id=${id}&currency_code=${currencyCode}`
    ),
  createCustom: (body: Record<string, any> | Record<string, any>[]) =>
    service.post(
      PAYMENT_SCHEDULES_ENDPOINTS.CREATE_CUSTOM,
      toPaymentSchedulePayload(body)
    ),
  selectCustomPaymentSchedules: (params: Record<string, any>) =>
    service.get(
      PAYMENT_SCHEDULES_ENDPOINTS.SELECT_CUSTOM_PAYMENT_SCHEDULES,
      params
    ),
  reviewPaymentSchedule: (
    id: string | number,
    type: "approve" | "decline",
    reason?: string
  ) => {
    const actionType = type === "approve" ? "Approve" : "Decline"
    const note = reason || (type === "approve" ? "approve" : '" "')
    return service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVIEW_PAYMENT_SCHEDULE}?id=${id}&note=${note}&actionType=${actionType}`
    )
  },
  reviewPaymentScheduleApproval: (
    id: string | number,
    type: "approve" | "decline",
    reason?: string
  ) => {
    const actionType = type === "approve" ? "Approve" : "Decline"
    const note = reason || (type === "approve" ? "approve" : '" "')
    return service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVIEW_PAYMENT_SCHEDULE_APPROVAL}?id=${id}&note=${note}&actionType=${actionType}`
    )
  },

  selectCustomPaymentSchedulesApproval: (params?: Record<string, any>) =>
    service.get(PAYMENT_SCHEDULES_ENDPOINTS.SELECT_CUSTOM_APPROVAL, params),

  reviewCustomApproval: (
    id: string | number,
    note: string,
    actionType: "Approve" | "Decline"
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVIEW_CUSTOM_APPROVAL}?id=${id}&note=${note}&actionType=${actionType}`
    ),
  batchReviewCustomApproval: (
    actionType: "Approve" | "Decline",
    data: Record<string, any>[]
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.BATCH_REVIEW_CUSTOM_APPROVAL}?id=${actionType}`,
      data
    ),

  reviewCustom: (
    id: string | number,
    note: string,
    actionType: "Approve" | "Decline"
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.REVIEW_CUSTOM}?id=${id}&note=${note}&actionType=${actionType}`
    ),
  createRegular: (id: string | number) =>
    service.post(`${PAYMENT_SCHEDULES_ENDPOINTS.CREATE_REGULAR}?id=${id}`),
  batchReviewCustom: (
    actionType: "Approve" | "Decline",
    data: Record<string, any>[]
  ) =>
    service.post(
      `${PAYMENT_SCHEDULES_ENDPOINTS.BATCH_REVIEW_CUSTOM}?id=${actionType}`,
      data
    ),
}
